import { RestClient, RestError } from "./restClient";

/** One row in the per-tenant connectors list. State is per-user:
 *  - "connected"   : user has a valid token in the vault
 *  - "needs_auth"  : enabled for tenant, user must run the OAuth dance
 *  - "disabled"    : not enabled by the tenant admin
 */
export interface ConnectorListing {
  name: string;
  display_name: string;
  description: string;
  category: string;
  ring: 1 | 2 | 3;
  state: "connected" | "needs_auth" | "disabled";
  /** Present when state == "needs_auth". */
  authorize_path?: string;
  display_when_disconnected?: string;
}

export interface AuthorizeResponse {
  authorize_url: string;
  redirect_uri: string;
}

/** Wraps the plugin_platform_service connector public surface plus the
 *  GET /plugins/v1/connectors listing endpoint. The listing endpoint is gracefully
 *  treated as "empty" on 404 so the UI works before the endpoint is deployed. */
export class ConnectorsApi {
  constructor(private client: RestClient) {}

  /** GET /plugins/v1/connectors — list per-tenant connectors with per-user state.
   *  Returns [] on 404 (endpoint not yet deployed). */
  async list(): Promise<ConnectorListing[]> {
    try {
      const r = await this.client.get<{ connectors: ConnectorListing[] }>(
        "/plugins/v1/connectors",
      );
      return r.connectors ?? [];
    } catch (e: unknown) {
      if (e instanceof RestError && e.status === 404) return [];
      throw e;
    }
  }

  /** POST /v1/connectors/{slug}/authorize — initiates OAuth dance.
   *  Returns the third-party authorize_url to open in a popup. */
  authorize(slug: string): Promise<AuthorizeResponse> {
    return this.client.post<AuthorizeResponse>(
      `/plugins/v1/connectors/${encodeURIComponent(slug)}/authorize`,
      {},
    );
  }

  /** DELETE /v1/connectors/{slug} — revokes the user's connection. */
  disconnect(slug: string): Promise<void> {
    return this.client.delete<void>(
      `/plugins/v1/connectors/${encodeURIComponent(slug)}`,
    );
  }

  /** GET /plugins/v1/connectors/config/{slug} — company connector config.
   *  Returns {configured, public}; secrets are never returned (write-only). */
  async getConfig(slug: string): Promise<ConnectorConfig> {
    try {
      return await this.client.get<ConnectorConfig>(
        `/plugins/v1/connectors/config/${encodeURIComponent(slug)}`,
      );
    } catch (e: unknown) {
      if (e instanceof RestError && e.status === 404)
        return { configured: false, public: {} };
      throw e;
    }
  }

  /** PUT /plugins/v1/connectors/config/{slug} — upsert a company's connector
   *  credential bundle. `secret` fields are encrypted at rest; `public` fields
   *  are stored plaintext + echoed back. Company admin only (403 otherwise). */
  putConfig(
    slug: string,
    body: { secret: Record<string, string>; public: Record<string, string> },
  ): Promise<{ status: string; connector_slug: string; public: Record<string, unknown> }> {
    return this.client.put(
      `/plugins/v1/connectors/config/${encodeURIComponent(slug)}`,
      body,
    );
  }
}

/** Company connector config view. Secrets omitted — write-only. */
export interface ConnectorConfig {
  configured: boolean;
  public: Record<string, unknown>;
}
